import Header from "../component/Nevbar";
import Footer from "../component/Footer";
import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "react-quill/dist/quill.snow.css";
import { useLanguage } from "../context/LanguageContext";

const BlogDetails = () => {
  const { language, fontFamilyStyle, lang } = useLanguage();
  const { id } = useParams();
  const location = useLocation();
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    const header = document.querySelector(".page-header");
    const toggleClass = "is-sticky";

    const handleScroll = () => {
      const currentScroll = window.scrollY || document.documentElement.scrollTop;
      if (currentScroll > 150) {
        header.classList.add(toggleClass);
      } else {
        header.classList.remove(toggleClass);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Load blog from router state, sessionStorage, or show fallback
  useEffect(() => {
    if (location.state?.blog) {
      setBlog(location.state.blog);
      sessionStorage.setItem(`blog_${id}`, JSON.stringify(location.state.blog));
    } else {
      const cached = sessionStorage.getItem(`blog_${id}`);
      if (cached) {
        setBlog(JSON.parse(cached));
      }
    }
  }, [id, location.state]);

  if (!blog) {
    return (
      <>
        <Header />
        <section className="pt-5 pb-5">
          <div className="container text-center">
            <p className="text-white">
              {language === "Arabic" ? "لم يتم العثور على المدونة." : "Blog post not found."}
            </p>
            <Link to={`/${lang}/blog`} className="invite_btn blue_bg text-white mt-3 d-inline-block">
              {language === "Arabic" ? "العودة إلى المدونة" : "Back to Blog"}
            </Link>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  const blogTitle = language === "Arabic" ? blog.ar_title : blog.title;
  const blogDescription = language === "Arabic" ? blog.ar_description : blog.description;
  const plainDescription = blogDescription
    ? blogDescription.replace(/<[^>]+>/g, "").slice(0, 160)
    : "";

  return (
    <>
      <Helmet>
        <html lang={lang === "en" ? "en" : "ar"} dir={lang === "en" ? "ltr" : "rtl"} />
        <title>{blogTitle ? `${blogTitle} | Khat Blog` : "Khat Blog"}</title>
        <meta name="description" content={plainDescription} />
        <link rel="canonical" href={`https://khatt.app/${lang}/blog/${id}`} />
        <link rel="alternate" hreflang="ar" href={`https://khatt.app/ar/blog/${id}`} />
        <link rel="alternate" hreflang="en" href={`https://khatt.app/en/blog/${id}`} />
        <link rel="alternate" hreflang="x-default" href={`https://khatt.app/ar/blog/${id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://khatt.app/${lang}/blog/${id}`} />
        <meta property="og:title" content={blogTitle || "Khat Blog"} />
        <meta property="og:description" content={plainDescription} />
        {blog.blog_image && <meta property="og:image" content={blog.blog_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blogTitle || "Khat Blog"} />
        <meta name="twitter:description" content={plainDescription} />
        {blog.blog_image && <meta name="twitter:image" content={blog.blog_image} />}
        {/* Article Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": blogTitle,
            "image": blog.blog_image || "",
            "datePublished": blog.insertdate,
            "author": {
              "@type": "Organization",
              "name": "Khat App"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Khat App",
              "logo": {
                "@type": "ImageObject",
                "url": "https://khatt.app/assets/imges/logo.svg"
              }
            },
            "description": plainDescription,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://khatt.app/${lang}/blog/${id}`
            }
          })}
        </script>
        {/* BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": language === "Arabic" ? "الرئيسية" : "Home",
                "item": `https://khatt.app/${lang}`
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": language === "Arabic" ? "المدونة" : "Blog",
                "item": `https://khatt.app/${lang}/blog`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": blogTitle
              }
            ]
          })}
        </script>
      </Helmet>
      <Header />

      <section className="pt-5 pb-5">
        <div className="container">
          <div className="col-12" style={{ fontFamily: fontFamilyStyle }}>
            {language === "Arabic" ? (
              <h6
                className="headline text-white pb_40 text-center"
                style={{ fontFamily: fontFamilyStyle }}
              >
                {blog?.ar_title}
              </h6>
            ) : (
              <h6
                className="headline text-white pb_40 text-center"
                style={{ fontFamily: fontFamilyStyle }}
              >
                {blog?.title}
              </h6>
            )}
            <div className="blog-detail-img">
              <img src={blog.blog_image} alt={blogTitle} />
            </div>
            <p className="text-white mb-3">
              <i className="fa-regular fa-calendar-days pe-2"></i>
              {language === "Arabic"
                ? new Date(blog.insertdate).toLocaleDateString("ar-EG")
                : new Date(blog.insertdate).toLocaleDateString()}
            </p>
            {language === "Arabic" ? (
              <div
                className="text-white mb-3  blog-description"
                style={{ direction: "rtl", fontFamily: "Noto Naskh Arabic, serif" }}
                dangerouslySetInnerHTML={{ __html: blog?.ar_description }}
              />
            ) : (
              <div
                className="text-white mb-3  blog-description"
                dangerouslySetInnerHTML={{ __html: blog?.description }}
              />
            )}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default BlogDetails;
